import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import InstallPrompt from "@/components/InstallPrompt";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import StaffList from "@/pages/StaffList";
import StaffDetail from "@/pages/StaffDetail";
import Councils from "@/pages/Councils";
import Schools from "@/pages/Schools";
import Children from "@/pages/Children";
import ChildWizard from "@/pages/ChildWizard";
import ChildDetail from "@/pages/ChildDetail";
import Notifications from "@/pages/Notifications";
import LiveStaffPage from "@/pages/LiveStaffPage";
import SubmittedReportsPage from "@/pages/SubmittedReportsPage";
import PoliciesPage from "@/pages/PoliciesPage";
import PolicyDetailPage from "@/pages/PolicyDetailPage";
import PolicyEditorPage from "@/pages/PolicyEditorPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AssessmentPage from "@/pages/AssessmentPage";
import JourneyPlanPage from "@/pages/JourneyPlanPage";
import TrainerJourneyView from "@/pages/TrainerJourneyView";
import ProgrammesPage from "@/pages/ProgrammesPage";
import ProgrammePage from "@/pages/ProgrammePage";
import SessionPage from "@/pages/SessionPage";
import SchedulePage from "@/pages/SchedulePage";
import SpotChecks from "@/pages/SpotChecks";
import IncidentsPage from "@/pages/IncidentsPage";
import IncidentNewPage from "@/pages/IncidentNewPage";
import IncidentDetailPage from "@/pages/IncidentDetailPage";
import SafeguardingPage from "@/pages/SafeguardingPage";
import SafeguardingNewPage from "@/pages/SafeguardingNewPage";
import SafeguardingDetailPage from "@/pages/SafeguardingDetailPage";
import FinalAssessmentPage from "@/pages/FinalAssessmentPage";
import ReportsPage from "@/pages/ReportsPage";
import Settings from "@/pages/Settings";
import AuditLogs from "@/pages/AuditLogs";
import Profile from "@/pages/Profile";
import Forbidden from "@/pages/Forbidden";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function RequirePermission({ perm, children }) {
  const { hasPermission } = useAuth();
  if (!hasPermission(perm)) return <Forbidden />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <InstallPrompt />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/children" element={<Children />} />
              <Route path="/children/new" element={<RequirePermission perm="children:create"><ChildWizard /></RequirePermission>} />
              <Route path="/children/:id" element={<ChildDetail />} />
              <Route path="/assessments/:assessmentId" element={<AssessmentPage />} />
              <Route path="/journey-plans/:planId" element={<JourneyPlanPage />} />
              <Route path="/journey-plans/:planId/trainer" element={<TrainerJourneyView />} />
              <Route path="/programmes" element={<ProgrammesPage />} />
              <Route path="/programmes/:programmeId" element={<ProgrammePage />} />
              <Route path="/sessions/:sessionId" element={<SessionPage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/spot-checks" element={<RequirePermission perm="spotchecks:read"><SpotChecks /></RequirePermission>} />
              <Route path="/incidents" element={<IncidentsPage />} />
              <Route path="/incidents/new" element={<IncidentNewPage />} />
              <Route path="/incidents/:incidentId" element={<IncidentDetailPage />} />
              <Route path="/safeguarding" element={<SafeguardingPage />} />
              <Route path="/safeguarding/new" element={<SafeguardingNewPage />} />
              <Route path="/safeguarding/:concernId" element={<SafeguardingDetailPage />} />
              <Route path="/final-assessments/:assessmentId" element={<FinalAssessmentPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/staff" element={<RequirePermission perm="staff:read"><StaffList /></RequirePermission>} />
              <Route path="/staff/:id" element={<StaffDetail />} />
              <Route path="/councils" element={<RequirePermission perm="councils:read"><Councils /></RequirePermission>} />
              <Route path="/schools" element={<RequirePermission perm="schools:read"><Schools /></RequirePermission>} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/live-staff" element={<RequirePermission perm="attendance:view_all"><LiveStaffPage /></RequirePermission>} />
              <Route path="/submitted-reports" element={<SubmittedReportsPage />} />
              <Route path="/policies" element={<PoliciesPage />} />
              <Route path="/policies/new" element={<RequirePermission perm="policies:manage"><PolicyEditorPage /></RequirePermission>} />
              <Route path="/policies/:policyId" element={<PolicyDetailPage />} />
              <Route path="/policies/:policyId/edit" element={<RequirePermission perm="policies:manage"><PolicyEditorPage /></RequirePermission>} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/settings" element={<RequirePermission perm="settings:manage"><Settings /></RequirePermission>} />
              <Route path="/audit-logs" element={<RequirePermission perm="audit:read"><AuditLogs /></RequirePermission>} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/403" element={<Forbidden />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
